import { inject, Injectable } from '@angular/core';
import { CandidateService } from 'src/app/recruiting/locums-margin-calculator/services/candidate.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { CandidateDetailsStore } from '../candidate-details/candidate-details.store';
import { tapResponse } from '@ngrx/operators';
import { debounceTime, map, of, switchMap } from 'rxjs';
import { initialCandidateDetailsSlice } from '../candidate-details/candidate-details.slice';
import { CandidateDetails } from '../../models/candidate-details.models';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from 'src/app/recruiting/locums-margin-calculator/store/enums/locums-margin-calculator-events';
import { CandidateSearchService } from 'src/app/recruiting/candidates/candidate-search/candidate-search.service';
import { CandidateSearchStore } from 'src/app/recruiting/locums-margin-calculator/store/candidate-search/candidate-search.store';
import { FetchArgs } from '@aya/controls/aya-table';
import { CandidateSearch } from 'src/app/recruiting/locums-margin-calculator/models/candidate-search.models';

@Injectable({ providedIn: 'root' })
export class CandidateEffects {
    private readonly candidateDetailsStore = inject(CandidateDetailsStore);
    private readonly candidateSearchStore = inject(CandidateSearchStore);
    private readonly candidateSearchService = inject(CandidateSearchService);
    private readonly candidateService = inject(CandidateService);
    private readonly eventBus = inject(EventBusService);

    constructor() {
        this.listenToCandidateSearchedEvent();
    }

    readonly loadCandidateDetails = rxMethod<number>((query$) =>
        query$.pipe(
            switchMap((candidateId: number) =>
                this.candidateService.getCandidateDetails(candidateId).pipe(
                    tapResponse({
                        next: (data: CandidateDetails) => {
                            data = { ...data, selectedSpecialtyId: data.specialties?.[0]?.id ?? 0 };
                            this.candidateDetailsStore.updateCandidateDetailsFromData({
                                ...data,
                                errorMessage: undefined
                            });
                        },
                        error: () =>
                            this.candidateDetailsStore.updateCandidateDetailsFromData({
                                ...initialCandidateDetailsSlice,
                                errorMessage: 'Failed to load candidate details.'
                            })
                    })
                )
            )
        )
    );

    readonly searchCandidates = rxMethod<string>((query$) =>
        query$.pipe(
            debounceTime(250),
            switchMap((searchText) => {
                const searchParams = {
                    textFilterLength: 2,
                    candidateSearchResultSize: 10,
                    defaultSort: { field: 'firstName', dir: 'asc' }
                };

                if (!searchText || searchText.length < searchParams.textFilterLength) {
                    this.candidateSearchStore.setCandidates([]);
                    return of([] as CandidateSearch[]);
                }

                const filter = this.candidateSearchService.createFilter(searchText);
                const fetchArgs = {
                    filter,
                    sort: searchParams.defaultSort,
                    skip: 0,
                    pageSize: searchParams.candidateSearchResultSize
                } as FetchArgs;

                return this.candidateService.rapidSearchLocumsCandidates(fetchArgs).pipe(
                    map((result) => {
                        result.forEach((x) => {
                            x.fullName = `${x.firstName} ${x.lastName}`;
                            x.recruiterFullName = `${x.travelRecruiterFirstName} ${x.travelRecruiterLastName}`?.trim();
                        });
                        this.candidateSearchStore.setCandidates(result);
                        return result;
                    })
                );
            })
        )
    );

    private listenToCandidateSearchedEvent() {
        this.eventBus.on(LocumsMarginCalculatorEvents.CANDIDATE_SEARCHED).subscribe((event: EventPayload) => {
            this.searchCandidates(event.payload);
        });
    }
}
